import { Camera } from "lucide-react";
import { dictionaries } from "../lib/i18n";
import type { GoldenHour, UiLang } from "../lib/types";

interface Props {
  golden: GoldenHour;
  lang: UiLang;
  /** Kompakt utgave til dagsoppsummeringen: én linje, ingen morgenvindu. */
  compact?: boolean;
}

/**
 * Når lyset er godt for å fotografere stedet.
 *
 * Den vanlige regelen er «den siste timen før solnedgang», og den er feil i
 * Norge. Vinduet varer så lenge sola bruker på å falle ti grader, og det
 * avhenger av breddegrad og årstid: 134 minutter på Hardangervidda i juni, 92 i
 * februar, 174 i Tromsø i november. Derfor står lengden her, ikke bare
 * klokkeslettet — det er lengden som avgjør om man må skynde seg.
 *
 * De tre særtilfellene er ikke feilmeldinger men svar. Mørketid har ikke lys å
 * fotografere i; høy midnattssol har hardt lys hele døgnet, som er det motsatte
 * av hva folk forventer; og en novemberdag i Tromsø er gyllen fra ende til
 * annen. Alle tre er verdt å si framfor å vise et klokkeslett som ikke betyr noe.
 */
export function GoldenHourNote({ golden, lang, compact }: Props) {
  const t = dictionaries[lang];

  if (golden.reason === "polarNight") {
    return compact ? null : <p className="golden golden--none">{t.goldenPolarNight}</p>;
  }
  if (golden.reason === "sunTooHigh") {
    return compact ? null : <p className="golden golden--none">{t.goldenSunTooHigh}</p>;
  }

  const evening = golden.evening;
  if (!evening) return null;

  return (
    <div className={`golden ${compact ? "golden--compact" : ""}`}>
      <span className="golden-icon">
        <Camera size={12} strokeWidth={2.2} />
      </span>

      <div className="golden-body">
        <strong>
          {t.goldenBest} {clock(evening.from, lang)}
        </strong>

        <small>
          {golden.allDay
            ? t.goldenAllDay(minutesLabel(evening.minutes, t))
            : golden.midnightSun
              ? t.goldenMidnightSun(minutesLabel(evening.minutes, t))
              : t.goldenWindow(
                  clock(evening.from, lang),
                  clock(evening.to, lang),
                  minutesLabel(evening.minutes, t),
                )}
        </small>

        {/*
          Morgenvinduet står under, dempet. Det er like godt lys, men man treffer
          det ved å stå opp framfor ved å være på plass, så det er et annet slags
          råd — og i den kompakte utgaven er det støy.
        */}
        {!compact && golden.morning && (
          <small className="golden-morning">
            {t.goldenMorning(
              clock(golden.morning.from, lang),
              clock(golden.morning.to, lang),
            )}
          </small>
        )}
      </div>
    </div>
  );
}

/**
 * Lengden på vinduet, som timer og minutter der det er over en time.
 *
 * «174 minutter» tvinger leseren til å regne. «2 t 54 min» gjør ikke det.
 */
function minutesLabel(minutes: number, t: (typeof dictionaries)["no"]): string {
  if (minutes < 60) return t.goldenMinutes(minutes);
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? t.goldenHours(hours) : t.goldenHoursMinutes(hours, rest);
}

/**
 * Klokkeslettet i brukerens egen tidssone.
 *
 * Tidene kommer som ISO i UTC fra serveren. `slice(11, 16)` ville vist UTC og
 * altså ligget to timer feil hele den norske sommeren — som for et fotoråd er
 * forskjellen mellom å rekke lyset og å komme etter at det er borte.
 */
function clock(iso: string, lang: UiLang): string {
  const stamp = new Date(iso);
  if (Number.isNaN(stamp.getTime())) return "";
  return stamp.toLocaleTimeString(lang === "no" ? "nb-NO" : "en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });
}
